import React, { useEffect, useMemo, useState } from 'react';
import { HiCheck, HiSearch, HiX } from 'react-icons/hi';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { SkeletonTable } from './Skeleton';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSAO_MAP } from '../types';
import './Clients.css';
import './SolicitacoesExclusao.css';

const STATUS_LABELS = {
  pending: 'Pendente',
  processed: 'Processada',
  rejected: 'Rejeitada',
};

const SolicitacoesExclusao = () => {
  const { userRole, loading: permissionsLoading } = usePermissions();
  const canManage = useMemo(() => {
    if (permissionsLoading || !userRole) return false;
    return PERMISSAO_MAP[Number(userRole.permissaos_id)] === 'Admin';
  }, [userRole, permissionsLoading]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [confirmAction, setConfirmAction] = useState(null);
  const { notifications, removeNotification, showSuccess, showWarning } = useNotification();

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, currentPage, itemsPerPage]);

  useRealtimeRefresh(['account_deletions', 'clients', 'users'], () => loadItems());

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAccountDeletionRequests({
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        page: currentPage,
        per_page: itemsPerPage,
      });
      const list = normalizeApiData(data);
      setItems(Array.isArray(list) ? list : []);
      setTotal(data?.total ?? (Array.isArray(list) ? list.length : 0));
      setLastPage(data?.last_page ?? 1);
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
      setItems([]);
      showWarning(error?.message || 'Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (row, action) => {
    if (!canManage) return;
    setConfirmAction({ row, action });
  };

  const runConfirm = async () => {
    if (!confirmAction?.row?.id) return;
    const { row, action } = confirmAction;
    try {
      if (action === 'process') {
        await apiService.processAccountDeletionRequest(row.id);
        showSuccess('Conta desativada. Remessas preservadas.');
      } else {
        await apiService.rejectAccountDeletionRequest(row.id);
        showSuccess('Solicitação rejeitada.');
      }
      setConfirmAction(null);
      loadItems();
    } catch (error) {
      showWarning(error?.message || 'Falha ao atualizar solicitação');
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('pt-BR');
    } catch {
      return value;
    }
  };

  return (
    <div className="page-container solicitacoes-exclusao">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Solicitações de exclusão</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Solicitações de exclusão</h1>
          <p>
            Pedidos do app SendingTo Cargo / site. Processar desativa a conta e anonimiza o cadastro;
            remessas e dados operacionais são mantidos.
          </p>
        </div>
      </div>

      <div className="page-actions exclusao-toolbar">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome, e-mail ou documento..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <select
          className="status-filter"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">Todos os status</option>
          <option value="pending">Pendentes</option>
          <option value="processed">Processadas</option>
          <option value="rejected">Rejeitadas</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <SkeletonTable rows={8} columns={7} />
        ) : items.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma solicitação encontrada</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Documento</th>
                    <th>Status</th>
                    <th>Solicitado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>
                        <strong>{row.nome || '—'}</strong>
                        {row.motivo ? (
                          <div className="motivo-preview" title={row.motivo}>
                            {row.motivo}
                          </div>
                        ) : null}
                      </td>
                      <td>{row.email || '—'}</td>
                      <td>{row.documento || '—'}</td>
                      <td>
                        <span className={`status-badge status-${row.status}`}>
                          {STATUS_LABELS[row.status] || row.status}
                        </span>
                      </td>
                      <td>{formatDate(row.created_at)}</td>
                      <td>
                        {row.status === 'pending' && canManage ? (
                          <div className="actions">
                            <button
                              className="btn-icon btn-approve"
                              title="Processar exclusão"
                              type="button"
                              onClick={() => openConfirm(row, 'process')}
                            >
                              <HiCheck size={18} />
                            </button>
                            <button
                              className="btn-icon btn-reject"
                              title="Rejeitar"
                              type="button"
                              onClick={() => openConfirm(row, 'reject')}
                            >
                              <HiX size={18} />
                            </button>
                          </div>
                        ) : (
                          <span className="muted">
                            {row.reviewed_at ? formatDate(row.reviewed_at) : '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <span>
                {total} registro{total === 1 ? '' : 's'}
              </span>
              <div className="pagination-controls">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  ‹
                </button>
                <span>
                  {currentPage} / {lastPage}
                </span>
                <button
                  type="button"
                  disabled={currentPage >= lastPage}
                  onClick={() => setCurrentPage((p) => Math.min(lastPage, p + 1))}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!confirmAction}
        title={
          confirmAction?.action === 'process'
            ? 'Processar exclusão de conta?'
            : 'Rejeitar solicitação?'
        }
        message={
          confirmAction?.action === 'process'
            ? `Desativar a conta de ${confirmAction?.row?.email}? Remessas e dados operacionais serão mantidos; o cadastro pessoal será anonimizado.`
            : `Rejeitar a solicitação de ${confirmAction?.row?.email}?`
        }
        confirmText={confirmAction?.action === 'process' ? 'Processar' : 'Rejeitar'}
        cancelText="Cancelar"
        type={confirmAction?.action === 'process' ? 'danger' : 'warning'}
        onConfirm={runConfirm}
        onCancel={() => setConfirmAction(null)}
      />

      <div className="notifications-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default SolicitacoesExclusao;
